import AntIcon from "@/components/icons/AntIcon";
import { useToken } from "@/utils/hooksUtils";
import { Button, Result } from "antd";
import React, { ErrorInfo } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useCreateAutomaticFrontendErrorLogBugReport } from "../../../common/api/queries";
import { useIsAuthenticated } from "../../../modules/auth/hooks";
import { DASHBOARD_ROUTE_PATHS } from "../../../modules/dashboard/paths";

interface Props {
  children: React.ReactNode;
}

export const ErrorBoundary = ({ children }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();

  const isUserAuthenticated = useIsAuthenticated();
  const createErrorLogBugReport = useCreateAutomaticFrontendErrorLogBugReport();

  const handleError = (error: unknown, errorInfo: ErrorInfo) => {
    const normalizedError = error instanceof Error ? error : new Error("Unknown error occurred.");

    if (isUserAuthenticated) {
      createErrorLogBugReport.mutate({
        name: normalizedError.name || "Unknown error",
        message: normalizedError.message || "Unknown error occurred.",
        stack: normalizedError.stack,
        componentStack: errorInfo.componentStack,
        locationPath: location.pathname + location.search,
      });
    }
  };

  return (
    <ReactErrorBoundary
      fallback={
        <Result
          className="result-page margin-top-large"
          status="500"
          title={<span style={{ color: token.colorPrimary }}>{t("navigation.error.title")}</span>}
          subTitle={t("navigation.error.text")}
          extra={
            <Button onClick={() => navigate(DASHBOARD_ROUTE_PATHS.homepage.to, { replace: true })}>
              {t("navigation.error.homeAction")}
              <AntIcon type="home" />
            </Button>
          }
        />
      }
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
};
